/**
 * VolunteerSignupForm — the unified cross-site volunteer signup form.
 *
 * Reused across FoodPulse, SocialPulse (and future sites). The parent
 * decides which context to render it in via the `siteOrigin` prop,
 * which is stored on the volunteer record so we know where each person
 * signed up. All data lands in the shared `volunteers` collection.
 *
 * After a successful signup we automatically fetch and display any
 * open volunteer opportunities that match the user's city + interests,
 * turning the signup into a real two-sided marketplace moment.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Heart, Loader2, Send, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const INTERESTS = [
  { value: "food-banks",         label: "Food banks" },
  { value: "soup-kitchens",      label: "Soup kitchens & community kitchens" },
  { value: "food-rescue",        label: "Food rescue & recovery" },
  { value: "grocery-delivery",   label: "Grocery shopping assistance" },
  { value: "home-delivery",      label: "Home food delivery" },
  { value: "community-gardens",  label: "Community gardens" },
  { value: "small-farms",        label: "Small farms & local agriculture" },
  { value: "disaster-relief",    label: "Disaster relief" },
  { value: "school-programs",    label: "Schools & youth programs" },
  { value: "senior-programs",    label: "Senior nutrition programs" },
  { value: "housing",            label: "Housing & shelter" },
  { value: "education",          label: "Education & tutoring" },
  { value: "health",             label: "Health & medical" },
  { value: "environment",        label: "Environment & cleanup" },
  { value: "animal-welfare",     label: "Animal welfare" },
  { value: "children-youth",     label: "Children & youth" },
  { value: "seniors",            label: "Seniors & elderly care" },
  { value: "legal-advocacy",     label: "Legal / advocacy" },
  { value: "corporate",          label: "Corporate volunteer days" },
  { value: "skills-based",       label: "Skills-based (design, IT, legal, etc.)" },
  { value: "community-events",   label: "Community events" },
  { value: "fundraising",        label: "Fundraising" },
  { value: "any",                label: "Anywhere I'm needed" },
];

const AVAILABILITY = [
  { value: "weekdays",   label: "Weekdays" },
  { value: "weekends",   label: "Weekends" },
  { value: "evenings",   label: "Evenings" },
  { value: "occasional", label: "Occasionally" },
  { value: "flexible",   label: "Flexible" },
];

// Palette adapts to the host site so the same form fits every brand.
const PALETTES = {
  foodpulse:   { accent: "#8B5E10", ink: "#3E2A0A", surface: "#F0E2C7", cream: "#FFF9EA" },
  socialpulse: { accent: "#7C3AED", ink: "#1E1B36", surface: "#EAE0FE", cream: "#F6F0FF" },
  ift:         { accent: "#0F3D2E", ink: "#0F3D2E", surface: "#DCEAD1", cream: "#F1F7EE" },
};

export default function VolunteerSignupForm({
  siteOrigin = "foodpulse",
  interestPreset = null,      // e.g. ["food-banks","food-rescue"] to pre-check
  compact = false,
  heading  = "Join the FoodPulse Volunteer Network",
  subheading = "One form. Every food-related volunteer opportunity in your city.",
  browseOpportunitiesHref = "/opportunities",
}) {
  const palette = PALETTES[siteOrigin] || PALETTES.foodpulse;

  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", country: "",
    availability: "flexible",
    interests: Array.isArray(interestPreset) ? interestPreset : [],
    skills: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);       // signup response
  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/volunteer/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, [done]);

  // Fetch personalised opportunities once the user has signed up.
  useEffect(() => {
    if (!done?.lead?.email && !done?.id) return;
    const email = done?.lead?.email || form.email.trim().toLowerCase();
    if (!email) return;
    setLoadingMatches(true);
    axios.get(`${API}/api/volunteer/my-opportunities`, { params: { email, limit: 4 } })
      .then((r) => setMatches(r.data.items || []))
      .catch(() => setMatches([]))
      .finally(() => setLoadingMatches(false));
  }, [done, form.email]);

  const canSubmit = useMemo(() =>
    form.name.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) &&
    form.city.trim().length >= 1,
  [form]);

  function toggleInterest(value) {
    setForm((f) => f.interests.includes(value)
      ? { ...f, interests: f.interests.filter((x) => x !== value) }
      : { ...f, interests: [...f.interests, value] });
  }

  async function submit(e) {
    e?.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/volunteer/signup`, {
        ...form,
        name:    form.name.trim(),
        email:   form.email.trim(),
        city:    form.city.trim(),
        country: form.country.trim() || null,
        skills:  form.skills.trim() || null,
        message: form.message.trim() || null,
        site_origin: siteOrigin,
      });
      setDone(res.data);
      toast.success(res.data.updated ? "Details updated." : "Welcome to the network!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't submit your signup. Try again.");
    } finally {
      setBusy(false);
    }
  }

  // ── Success view ────────────────────────────────────────────────
  if (done) {
    return (
      <div className="rounded-2xl border bg-white p-6 sm:p-8 space-y-6"
           style={{ borderColor: "#E9D8B4" }}
           data-testid="volunteer-signup-done">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-8 h-8 flex-none" style={{ color: "#166534" }} />
          <div>
            <div className="font-display text-2xl mb-1" style={{ color: palette.ink }}>
              {done.updated ? "Details updated" : "Thank you for joining!"}
            </div>
            <p className="text-sm opacity-80 max-w-lg">{done.message}</p>
          </div>
        </div>

        {/* Personalised opportunity feed */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">
            Opportunities matched to your city & interests
          </div>
          {loadingMatches ? (
            <div className="text-sm opacity-60 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Finding matches…
            </div>
          ) : matches.length === 0 ? (
            <div className="text-sm opacity-70 rounded-lg border border-dashed p-4"
                 style={{ borderColor: palette.accent, background: palette.cream }}>
              No live matches in your city yet — we'll email you the moment one is posted.
              In the meantime,{" "}
              <Link to={browseOpportunitiesHref} className="underline font-semibold"
                    style={{ color: palette.accent }}
                    data-testid="volunteer-browse-all-fallback">
                browse every open opportunity
              </Link>.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="volunteer-matches">
              {matches.map((op) => (
                <Link key={op.id} to={`${browseOpportunitiesHref}?highlight=${op.id}`}
                      className="block rounded-lg border p-4 hover:shadow-md transition-all hover:-translate-y-0.5 bg-white"
                      style={{ borderColor: "#E9D8B4" }}
                      data-testid={`volunteer-match-${op.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full"
                          style={{ background: palette.surface, color: palette.accent }}>
                      {op.category || "volunteer"}
                    </span>
                    <ArrowRight className="w-3 h-3 opacity-40" />
                  </div>
                  <div className="font-display text-base leading-tight mb-1"
                       style={{ color: palette.ink }}>
                    {op.title}
                  </div>
                  <div className="text-xs opacity-70 line-clamp-2">
                    {op.description}
                  </div>
                  <div className="mt-2 text-[10px] font-mono opacity-70">
                    {op.city?.toUpperCase()} · {op.hours}h/week
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <button type="button"
                    onClick={() => { setDone(null); setForm({ ...form, message: "" }); }}
                    className="text-xs underline opacity-70 hover:opacity-100"
                    data-testid="volunteer-signup-update">
              Update my details
            </button>
            <Link to={browseOpportunitiesHref}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                  style={{ color: palette.accent }}
                  data-testid="volunteer-browse-all">
              Browse every open opportunity <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}
          className="rounded-2xl border bg-white overflow-hidden"
          style={{ borderColor: "#E9D8B4" }}
          data-testid="volunteer-signup-form">
      <div className="p-5 sm:p-6 border-b flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
           style={{ borderColor: "#E9D8B4",
                    background: `linear-gradient(90deg, ${palette.surface} 0%, ${palette.cream} 100%)` }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-none"
             style={{ background: palette.accent }}>
          <Heart className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl leading-tight" style={{ color: palette.ink }}>
            {heading}
          </div>
          <div className="text-sm opacity-80">
            {subheading}
          </div>
        </div>
        {stats?.total_volunteers > 0 && (
          <div className="text-right flex-none">
            <div className="text-2xl font-display font-bold" style={{ color: palette.accent }}
                 data-testid="volunteer-stats-total">
              {stats.total_volunteers.toLocaleString()}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">
              volunteers joined
            </div>
          </div>
        )}
      </div>

      <div className={`p-5 sm:p-6 ${compact ? "space-y-3" : "space-y-4"}`}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Full name">
            <input type="text" required minLength={2} value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                   style={{ borderColor: "#E9D8B4" }}
                   placeholder="Anita Menon"
                   data-testid="volunteer-name-input" />
          </Field>
          <Field label="Email">
            <input type="email" required value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                   style={{ borderColor: "#E9D8B4" }}
                   placeholder="anita@example.com"
                   data-testid="volunteer-email-input" />
          </Field>
          <Field label="City">
            <input type="text" required value={form.city}
                   onChange={(e) => setForm({ ...form, city: e.target.value })}
                   className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                   style={{ borderColor: "#E9D8B4" }}
                   placeholder="Kochi"
                   data-testid="volunteer-city-input" />
          </Field>
          <Field label="Country (optional)">
            <input type="text" value={form.country}
                   onChange={(e) => setForm({ ...form, country: e.target.value })}
                   className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                   style={{ borderColor: "#E9D8B4" }}
                   placeholder="India"
                   data-testid="volunteer-country-input" />
          </Field>
          <Field label="Phone (optional)">
            <input type="tel" value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                   style={{ borderColor: "#E9D8B4" }}
                   placeholder="+91 98765 43210"
                   data-testid="volunteer-phone-input" />
          </Field>
          <Field label="Availability">
            <select value={form.availability}
                    onChange={(e) => setForm({ ...form, availability: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                    style={{ borderColor: "#E9D8B4" }}
                    data-testid="volunteer-availability-input">
              {AVAILABILITY.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">
            What would you like to help with? (pick any)
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="volunteer-interests">
            {INTERESTS.map((i) => {
              const on = form.interests.includes(i.value);
              return (
                <button type="button" key={i.value} onClick={() => toggleInterest(i.value)}
                        className="text-xs px-3 py-1 rounded-full border transition-all hover:shadow-sm"
                        style={on
                          ? { background: palette.accent, borderColor: palette.accent, color: "#fff" }
                          : { background: "#fff", borderColor: "#E9D8B4", color: palette.ink }}
                        data-testid={`volunteer-interest-${i.value}`}>
                  {i.label}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Skills you'd like to contribute (optional)">
          <input type="text" value={form.skills}
                 onChange={(e) => setForm({ ...form, skills: e.target.value })}
                 className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                 style={{ borderColor: "#E9D8B4" }}
                 placeholder="Photography, event planning, translation, driving…"
                 data-testid="volunteer-skills-input" />
        </Field>

        <Field label="Anything else you'd like us to know? (optional)">
          <textarea rows={3} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                    style={{ borderColor: "#E9D8B4" }}
                    placeholder="Prefer weekend mornings, own a car, comfortable working with children, etc."
                    data-testid="volunteer-message-input" />
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="text-[10px] opacity-60 max-w-md">
            By joining you agree FoodPulse & SocialPulse can share your interest with
            vetted local charities. You'll always be able to opt out or update your details.
          </div>
          <button type="submit" disabled={!canSubmit || busy}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-50 hover:shadow-md transition-shadow"
                  style={{ background: palette.accent }}
                  data-testid="volunteer-signup-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Submitting…" : "Sign me up"}
          </button>
        </div>
      </div>
    </form>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

